import { FieldConfig } from '../ModalFieldRenderer';
import { Facility } from '../../../services/api/domains/facilities/types';
import { validationHelpers } from '../utils/validation';
import {
  formatDateForInput,
  formatDateTime,
  parseDateFromInput,
} from '../../../utils/dateConversion';
import { ManufacturerSummary } from 'services/api/domains/products/types';

export type FacilityFieldOptions = {
  manufacturers?: ManufacturerSummary[];
};

export const getFacilityFields = (
  opts: FacilityFieldOptions = {}
): FieldConfig<Facility>[] => [
  {
    key: 'facility_name',
    label: 'Facility Name',
    type: 'text',
    getValue: f => f.facility_name || '',
    setValue: (f, val) => ({ ...f, facility_name: val }),
    validator: validationHelpers.combine(
      validationHelpers.required,
      validationHelpers.minLength(2)
    ),
    isRequired: true,
  },
  {
    key: 'manufacturer_id',
    label: 'Manufacturer',
    type: 'select',
    getValue: p => p.manufacturer_id?.toString() || '',
    setValue: (p, val) => ({
      ...p,
      manufacturer_id: parseInt(val, 10),
      manufacturer_name: '',
    }),
    options:
      opts.manufacturers?.map(m => ({
        value: m.manufacturer_id?.toString() || '',
        label: m.manufacturer_name || '',
      })) || [],
    isRequired: false,
  },
  {
    key: 'fei_number',
    label: 'FEI Number',
    type: 'text',
    getValue: f => f.fei_number || '',
    setValue: (f, val) => ({ ...f, fei_number: val }),
  },
  {
    key: 'duns_number',
    label: 'DUNS Number',
    type: 'text',
    getValue: f => f.duns_number || '',
    setValue: (f, val) => ({ ...f, duns_number: val }),
  },
  {
    key: 'qta_status',
    label: 'QTA Status',
    type: 'text',
    getValue: f => f.qta_status || '',
    setValue: (f, val) => ({ ...f, qta_status: val }),
  },
  {
    key: 'qta_effective_date',
    label: 'QTA Effective Date',
    type: 'date',
    getValue: f => formatDateForInput(f.qta_effective_date),
    setValue: (f, val) => ({
      ...f,
      qta_effective_date: parseDateFromInput(val) || null,
    }),
    helpText: 'Date when QTA status becomes effective',
  },
  {
    key: 'qta_expiration_date',
    label: 'QTA Expiration Date',
    type: 'date',
    getValue: f => formatDateForInput(f.qta_expiration_date),
    setValue: (f, val) => ({
      ...f,
      qta_expiration_date: parseDateFromInput(val) || null,
    }),
    validator: validationHelpers.futureDate,
    helpText: 'Date when QTA status expires (must be in the future)',
  },
  {
    key: 'audit_date',
    label: 'Audit Date',
    type: 'date',
    getValue: f => formatDateForInput(f.audit_date),
    setValue: (f, val) => ({
      ...f,
      audit_date: parseDateFromInput(val) || null,
    }),
  },
  {
    key: 'address_line1',
    label: 'Address Line 1',
    type: 'text',
    getValue: f => f.address_line1 || '',
    setValue: (f, val) => ({ ...f, address_line1: val }),
  },
  {
    key: 'address_line2',
    label: 'Address Line 2',
    type: 'text',
    getValue: f => f.address_line2 || '',
    setValue: (f, val) => ({ ...f, address_line2: val }),
  },
  {
    key: 'city',
    label: 'City',
    type: 'text',
    getValue: f => f.city || '',
    setValue: (f, val) => ({ ...f, city: val }),
  },
  {
    key: 'state_province',
    label: 'State/Province',
    type: 'text',
    getValue: f => f.state_province || '',
    setValue: (f, val) => ({ ...f, state_province: val }),
  },
  {
    key: 'postal_code',
    label: 'Postal Code',
    type: 'text',
    getValue: f => f.postal_code || '',
    setValue: (f, val) => ({ ...f, postal_code: val }),
  },
  {
    key: 'country_code',
    label: 'Country Code',
    type: 'text',
    getValue: f => f.country_code || '',
    setValue: (f, val) => ({ ...f, country_code: val }),
  },
  {
    key: 'latitude',
    label: 'Latitude',
    type: 'number',
    getValue: f => f.latitude || null,
    setValue: (f, val) => ({ ...f, latitude: val }),
    min: -90,
    max: 90,
    step: 0.000001,
    validator: validationHelpers.latitude,
    helpText: 'Decimal degrees (-90 to 90)',
  },
  {
    key: 'longitude',
    label: 'Longitude',
    type: 'number',
    getValue: f => f.longitude || null,
    setValue: (f, val) => ({ ...f, longitude: val }),
    min: -180,
    max: 180,
    step: 0.000001,
    validator: validationHelpers.longitude,
    helpText: 'Decimal degrees (-180 to 180)',
  },
  {
    key: 'comments',
    label: 'Comments',
    type: 'textarea',
    getValue: f => f.comments || '',
    setValue: (f, val) => ({ ...f, comments: val }),
    rows: 4,
  },
  {
    key: 'facility_status',
    label: 'Facility Status',
    type: 'select',
    getValue: f => f.facility_status || '',
    setValue: (f, val) => ({ ...f, facility_status: val }),
    options: [
      { value: 'APPROVED', label: 'APPROVED' },
      { value: 'INACTIVE', label: 'INACTIVE' },
      { value: 'DISQUALIFY', label: 'DISQUALIFY' },
      { value: 'R&D ONLY', label: 'R&D ONLY' },
      { value: 'ON HOLD', label: 'ON HOLD' },
      { value: 'NOT MANUF.', label: 'NOT MANUF.' },
    ],
    isRequired: false,
  },
  {
    key: 'created_date',
    label: 'Created',
    type: 'readonly',
    getValue: f => f.created_date || '',
    formatter: formatDateTime,
    isAlwaysDisabled: true,
  },
  {
    key: 'modified_date',
    label: 'Last Updated',
    type: 'readonly',
    getValue: f => f.modified_date || '',
    formatter: formatDateTime,
    isAlwaysDisabled: true,
  },
  {
    key: 'user_id',
    label: 'User ID',
    type: 'readonly',
    getValue: f => f.user_id || '',
    isAlwaysDisabled: true,
  },
];

// Special readonly fields that might need custom logic
export const facilityIdField: FieldConfig<Facility> = {
  key: 'facility_id',
  label: 'Facility ID',
  type: 'readonly',
  getValue: f => f.facility_id?.toString() || '',
  isAlwaysDisabled: true,
};

// Always disabled field names for facilities
export const facilityAlwaysDisabledFields = [
  'facility_id',
  'created_date',
  'modified_date',
  'user_id',
];
